import { z } from 'zod';

export type AppConfig = {
  apiBaseUrl: string;
  adminUserId?: string;
  sessionMaxAgeMs?: number;
  shouldMaskSnapshots?: boolean;
  publicSite: {
    variant: 'current' | 'discoverability';
    pages: {
      docs: boolean;
      features: boolean;
      pricing: boolean;
    };
  };
  errors?: string[];
};

const defaultApiBaseUrl = 'http://localhost:3000';
const defaultPublicSiteVariant = 'current' as const;

const parseBooleanFlag = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined || value.trim() === '') {
    return defaultValue;
  }
  return value.trim().toLowerCase() === 'true';
};

const EnvSchema = z.object({
  VITE_API_BASE_URL: z
    .preprocess((val) => (val === undefined || val === '' ? defaultApiBaseUrl : val), z.url())
    .default(defaultApiBaseUrl),
  VITE_ADMIN_USER_ID: z.string().trim().optional(),
  VITE_SESSION_MAX_AGE_MINUTES: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      if (val === undefined || val === '') return undefined;
      const num = Number(val);
      if (Number.isNaN(num)) {
        throw new Error('VITE_SESSION_MAX_AGE_MINUTES must be a number');
      }
      return num * 60 * 1000;
    }),
  VITE_MASK_SNAPSHOTS: z.string().optional(),
  VITE_PUBLIC_SITE_VARIANT: z.enum(['current', 'discoverability']).optional(),
  VITE_PUBLIC_DOCS_ENABLED: z.string().optional(),
  VITE_PUBLIC_FEATURES_ENABLED: z.string().optional(),
  VITE_PUBLIC_PRICING_ENABLED: z.string().optional(),
});

const parseEnv = (): { config: AppConfig; errors: string[] } => {
  const result = EnvSchema.safeParse(import.meta.env);
  if (!result.success) {
    const messages = result.error.issues.map((issue) => {
      const path = issue.path.join('.') || 'env';
      return `${path}: ${issue.message}`;
    });
    return {
      config: {
        apiBaseUrl: '',
        publicSite: {
          variant: defaultPublicSiteVariant,
          pages: {
            docs: true,
            features: false,
            pricing: false,
          },
        },
        errors: messages,
      },
      errors: messages,
    };
  }
  const data = result.data;
  const publicFeaturesEnabled =
    data.VITE_PUBLIC_FEATURES_ENABLED ?? data.VITE_PUBLIC_PRICING_ENABLED;
  const publicPricingEnabled =
    data.VITE_PUBLIC_PRICING_ENABLED ?? data.VITE_PUBLIC_FEATURES_ENABLED;
  const errors: string[] = [];
  const isProd = import.meta.env.PROD;
  const rawApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (isProd) {
    const rawValue = typeof rawApiBaseUrl === 'string' ? rawApiBaseUrl.trim() : '';
    if (!rawValue || rawValue === defaultApiBaseUrl) {
      errors.push('VITE_API_BASE_URL must be set to a non-localhost URL in production.');
    }
  }
  return {
    config: {
      apiBaseUrl: data.VITE_API_BASE_URL,
      adminUserId: data.VITE_ADMIN_USER_ID?.trim() ?? undefined,
      sessionMaxAgeMs: data.VITE_SESSION_MAX_AGE_MINUTES,
      shouldMaskSnapshots: (data.VITE_MASK_SNAPSHOTS ?? '').toLowerCase() === 'true',
      publicSite: {
        variant: data.VITE_PUBLIC_SITE_VARIANT ?? defaultPublicSiteVariant,
        pages: {
          docs: parseBooleanFlag(data.VITE_PUBLIC_DOCS_ENABLED, true),
          features: parseBooleanFlag(publicFeaturesEnabled, false),
          pricing: parseBooleanFlag(publicPricingEnabled, false),
        },
      },
    },
    errors,
  };
};

const parsed = parseEnv();

export const config: AppConfig = { ...parsed.config, errors: parsed.errors };
